import { useCallback, useEffect, useRef, useState } from 'react';
import { buildColor, extractPaletteFromImage, MAX_CANVAS_DIM } from './colorPickerImageEngine';

/**
 * Renders the uploaded image and turns pointer interactions into color picks.
 * Pixel colors are sampled from a hidden canvas that mirrors the image.
 */
export default function ColorPickerCanvas({ file, alt, onPick, onPaletteReady, onLoadError }) {
    const imgRef = useRef(null);
    const wrapperRef = useRef(null);
    const samplerRef = useRef(null);

    const [previewUrl, setPreviewUrl] = useState(null);
    const [hover, setHover] = useState(null);

    useEffect(() => {
        if (!file) return undefined;

        const url = URL.createObjectURL(file);
        let cancelled = false;

        const img = new Image();
        img.onload = () => {
            if (cancelled) return;
            const scale = Math.min(1, MAX_CANVAS_DIM / Math.max(img.naturalWidth, img.naturalHeight));
            const width = Math.max(1, Math.round(img.naturalWidth * scale));
            const height = Math.max(1, Math.round(img.naturalHeight * scale));

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(img, 0, 0, width, height);

            samplerRef.current = { ctx, width, height };
            setPreviewUrl(url);
            onPaletteReady?.(extractPaletteFromImage(img));
        };
        img.onerror = () => {
            if (!cancelled) onLoadError?.();
        };
        img.src = url;

        return () => {
            cancelled = true;
            samplerRef.current = null;
            setPreviewUrl(null);
            setHover(null);
            URL.revokeObjectURL(url);
        };
    }, [file, onLoadError, onPaletteReady]);

    const colorAtPointer = useCallback((clientX, clientY) => {
        const sampler = samplerRef.current;
        const img = imgRef.current;
        if (!sampler || !img) return null;

        const rect = img.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return null;

        const x = Math.min(
            sampler.width - 1,
            Math.max(0, Math.floor(((clientX - rect.left) / rect.width) * sampler.width)),
        );
        const y = Math.min(
            sampler.height - 1,
            Math.max(0, Math.floor(((clientY - rect.top) / rect.height) * sampler.height)),
        );

        const [r, g, b] = sampler.ctx.getImageData(x, y, 1, 1).data;
        return buildColor(r, g, b);
    }, []);

    const handleMouseMove = (e) => {
        const color = colorAtPointer(e.clientX, e.clientY);
        if (!color) return;

        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        setHover({
            color,
            x: e.clientX - wrapperRect.left,
            y: e.clientY - wrapperRect.top,
        });
    };

    const handleClick = (e) => {
        const color = colorAtPointer(e.clientX, e.clientY);
        if (color) onPick(color);
    };

    if (!previewUrl) return null;

    return (
        <div ref={wrapperRef} className="relative flex h-full w-full items-center justify-center overflow-hidden">
            <img
                ref={imgRef}
                src={previewUrl}
                alt={alt}
                draggable={false}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setHover(null)}
                onClick={handleClick}
                className="max-h-full max-w-full cursor-crosshair select-none rounded-lg object-contain shadow-[0_1px_3px_rgba(28,24,21,0.12)]"
            />
            {hover ? (
                <div
                    className="pointer-events-none absolute z-10 flex items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white/95 px-2 py-1 shadow-md backdrop-blur-sm dark:border-zinc-700 dark:bg-zinc-900/95"
                    style={{ left: hover.x + 14, top: hover.y + 14 }}
                >
                    <span
                        className="h-4 w-4 flex-none rounded border border-black/10 dark:border-white/20"
                        style={{ backgroundColor: hover.color.hex }}
                    />
                    <span className="font-mono text-[11px] font-semibold text-[#3D3833] dark:text-zinc-200">
                        {hover.color.hex}
                    </span>
                </div>
            ) : null}
        </div>
    );
}
